import React, { useState, useEffect } from "react";
import { Cookie, X as XIcon, Check } from "lucide-react";

export default function GDPRBanner() {
  const [show, setShow] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,  // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("gdpr_consent");
    if (!consent) {
      // Show banner after 1 second delay
      setTimeout(() => setShow(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("gdpr_consent", JSON.stringify(consent));
    setShow(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("gdpr_consent", JSON.stringify(consent));
    setShow(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("gdpr_consent", JSON.stringify(consent));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-sm border-t border-zinc-800 z-40 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {!showPreferences ? (
          // Simple banner
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie size={24} className="text-emerald-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Cookie Consent</h3>
                <p className="text-sm text-zinc-400">
                  We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                  By clicking &quot;Accept All&quot;, you consent to our use of cookies.{" "}
                  <a href="/privacy" className="text-emerald-500 hover:underline">
                    Privacy Policy
                  </a>
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-sm transition"
              >
                Customize
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-sm transition"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-sm font-semibold transition"
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          // Preferences panel
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Cookie size={20} className="text-emerald-500" />
                Cookie Preferences
              </h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="p-1 hover:bg-zinc-800 rounded transition"
              >
                <XIcon size={20} />
              </button>
            </div>
            
            <div className="space-y-3 mb-4">
              {/* Necessary cookies */}
              <div className="flex items-start justify-between p-3 bg-zinc-800/50 rounded">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Check size={16} className="text-emerald-500" />
                    <span className="font-semibold text-sm">Necessary Cookies</span>
                    <span className="text-xs text-zinc-500">(Always Active)</span>
                  </div>
                  <p className="text-xs text-zinc-400">
                    Essential for the website to function properly. Cannot be disabled.
                  </p>
                </div>
              </div>
              
              {/* Analytics cookies */}
              <div className="flex items-start justify-between p-3 bg-zinc-800/50 rounded">
                <div className="flex-1 pr-4">
                  <span className="font-semibold text-sm block mb-1">Analytics Cookies</span>
                  <p className="text-xs text-zinc-400">
                    Help us understand how visitors interact with the website.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>
              
              {/* Marketing cookies */}
              <div className="flex items-start justify-between p-3 bg-zinc-800/50 rounded">
                <div className="flex-1 pr-4">
                  <span className="font-semibold text-sm block mb-1">Marketing Cookies</span>
                  <p className="text-xs text-zinc-400">
                    Used to track visitors across websites for marketing purposes.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-sm transition"
              >
                Reject All
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-sm font-semibold transition"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
